import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Injectable,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  DateAdapter,
  NativeDateAdapter,
  provideNativeDateAdapter,
} from '@angular/material/core';
import {
  MatCalendar,
  MatCalendarCellClassFunction,
  MatDatepickerModule
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { HighlightedDates } from '../../data/models/highilighted-dates.model';
import { isSameDate } from '../../util/shared.util';

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1;
  }

  override getDayOfWeekNames(_: 'long' | 'short' | 'narrow'): string[] {
    return super.getDayOfWeekNames('short').map(day => day.slice(0, 2));
  }
}

@Component({
  selector: 'app-date-picker-dialog',
  templateUrl: './date-picker-dialog.component.html',
  styleUrl: './date-picker-dialog.component.scss',
  imports: [
    CommonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatCardModule
  ],
  encapsulation: ViewEncapsulation.None,
  providers: [
    provideNativeDateAdapter(),
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
})
export class DatePickerDialogComponent implements OnChanges, OnInit, AfterViewInit {
  @Input() highlightDates!: HighlightedDates[];
  @Input() languageCode!: 'da-DK' | 'en-GB' | 'sv-SE';
  @Input() selected = new Date();
  @ViewChild('calendar', { static: true }) calendar!: MatCalendar<Date>;

  dialogRef = inject(MatDialogRef<DatePickerDialogComponent>);

  private selectedDate = new Date();

  get nextMonthDisabled(): boolean {
    const currentMonth = this.selectedDate.getMonth() === new Date().getMonth();
    const currentYear = this.selectedDate.getFullYear() === new Date().getFullYear();

    return currentMonth && currentYear;
  }

  get monthName(): string {
    const currentYear = new Date().getFullYear();
    const selectedYear = this.selectedDate.getFullYear();
    const month = this.selectedDate.toLocaleString(this.languageCode, { month: 'short' }).slice(0, 3).toUpperCase();
    const year = this.selectedDate.toLocaleString('default', { year: '2-digit' });

    if (selectedYear < currentYear) {
      return `${month} '${year}`;
    }

    return month;
  }

  constructor(private dateAdapter: DateAdapter<Date>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['languageCode'].currentValue) {
      this.languageCode = changes['languageCode'].currentValue;
      this.setLocale();
    }
  }

  ngOnInit(): void {
    this.setLocale();
  }

  ngAfterViewInit(): void {
    this.selectedDate = this.selected;
    this.calendar.activeDate = this.selected;
    this.calendar.updateTodaysDate();
  }

  dateFilter = (date: Date): boolean => {
    const selectedIndex = this.highlightDates.findIndex((highlightedDate) => isSameDate(highlightedDate.date, date, false));

    return selectedIndex !== -1;
  };

  dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    if (view === 'month' && this.highlightDates) {
      let cssClass = '';
      const highlight = this.highlightDates.find(highlightedDate =>
        highlightedDate.date.toDateString() === cellDate.toDateString()
      );

      if (!highlight) {
        return '';
      }

      if (highlight) {
        cssClass = 'highlighted ';
      }

      if (highlight?.hasNote) {
        cssClass += 'note ';
      }

      return cssClass + highlight?.color;
    }

    return '';
  };

  moveBack(): void {
    const date = this.selectedDate;
    this.selectedDate = new Date(date.getFullYear(), date.getMonth() - 1);
    this.calendar.activeDate = this.selectedDate;
    this.calendar.updateTodaysDate();
  }

  moveForward(): void {
    if (this.nextMonthDisabled) {
      return;
    }

    const date = this.selectedDate;
    this.selectedDate = new Date(date.getFullYear(), date.getMonth() + 1);
    this.calendar.activeDate = this.selectedDate;
    this.calendar.updateTodaysDate();
  }

  onDateSelected(date: Date | null): void {
    if (!date) {
      return;
    }

    const selectedIndex = this.highlightDates.findIndex((highlightedDate) => isSameDate(highlightedDate.date, date, false));

    if (selectedIndex === -1) {
      return;
    }

    this.selected = this.highlightDates[selectedIndex].date;
    this.dialogRef.close(this.selected);
  }

  private setLocale(): void {
    this.dateAdapter.setLocale(this.languageCode);
  }
}